import React, { useCallback, useEffect, useState } from 'react';
import { Icon, ChannelBadge } from './inboxIcons';

const STEPS = ['IMPORTED', 'ALLOCATING', 'PRODUCTION', 'READY_TO_PACK', 'PACKED', 'IN_TRANSIT', 'DELIVERED'];
const STEP_LABEL = { IMPORTED: 'Received', ALLOCATING: 'In queue', PRODUCTION: 'Printing', READY_TO_PACK: 'Ready to pack', PACKED: 'Packed', IN_TRANSIT: 'Shipped', DELIVERED: 'Delivered' };
const STATUS_LABEL = { new: 'New', open: 'In progress', waiting: 'Waiting on customer', resolved: 'Resolved' };

const title = (s) => String(s || '').replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
const nameOf = (c) => c?.displayName || [c?.firstName, c?.lastName].filter(Boolean).join(' ') || c?.email || 'Customer';
const money = (v, cur) => (v == null || v === '' ? '—' : `${cur && cur !== 'INR' ? `${cur} ` : '₹'}${Math.round(Number(v) || 0).toLocaleString('en-IN')}`);

function fmtDate(v) {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });
}

function fulfilTone(s) {
  const st = String(s || '').toLowerCase();
  if (st === 'fulfilled' || st === 'delivered') return 'cx-c-resolved';
  if (st === 'cancelled' || st === 'refunded') return 'cx-c-plain';
  if (st === 'partially_fulfilled') return 'cx-c-waiting';
  return 'cx-c-open';
}

function Production({ api, orderId }) {
  const [data, setData] = useState(undefined);
  useEffect(() => {
    let alive = true;
    setData(undefined);
    api(`/api/v1/support/orders/${encodeURIComponent(orderId)}/production-status`)
      .then((d) => alive && setData(d))
      .catch(() => alive && setData(null));
    return () => { alive = false; };
  }, [api, orderId]);

  if (data === undefined) return <div className="cx-skel" style={{ height: 90, margin: '0 18px 16px' }} />;
  if (!data || !data.orderStatus) return <div className="cx-panel-b cx-hint">Production details aren't available for this order.</div>;
  const cur = String(data.orderStatus).toUpperCase();
  const idx = STEPS.indexOf(cur);
  return (
    <div className="cx-panel-b" style={{ display: 'grid', gap: 14 }}>
      <div className="cx-steps" aria-label={`Order stage: ${STEP_LABEL[cur] || title(cur)}`}>
        {STEPS.map((s, i) => <div key={s} className={i < idx ? 'done' : i === idx ? 'now' : ''}><i />{STEP_LABEL[s]}</div>)}
      </div>
      {idx < 0 && <span className="cx-chip cx-c-new" style={{ justifySelf: 'start' }}>{title(cur)}</span>}
      {data.lines?.length > 0 && (
        <div className="cx-scroll-x" style={{ border: '1px solid var(--line)', borderRadius: 8 }}>
          <table className="cx-table">
            <thead><tr><th>SKU</th><th>Status</th><th className="r">Needed</th><th className="r">In batch</th><th className="r">Received</th><th className="r">Packed</th><th className="r">Shipped</th></tr></thead>
            <tbody>
              {data.lines.map((l, i) => (
                <tr key={l.sku || i}>
                  <td className="cx-mono">{l.sku || '—'}</td>
                  <td><span className="cx-chip cx-c-plain">{title(l.lineStatus)}</span></td>
                  {['netRequiredQty', 'inBatchQty', 'receivedQty', 'packedQty', 'shippedQty'].map((k) => <td key={k} className="r cx-num">{l[k] ?? '—'}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13, color: 'var(--ink-2)' }}>
        {data.batchId && <span>Batch <b className="cx-mono" style={{ fontWeight: 500 }}>{data.batchId}</b>{data.batchStatus ? ` · ${title(data.batchStatus)}` : ''}</span>}
        {data.shipment && <span><Icon name="truck" size={15} /> <b className="cx-mono" style={{ fontWeight: 500 }}>{data.shipment.awb || '—'}</b>{data.shipment.carrier ? ` via ${data.shipment.carrier}` : ''}{data.shipment.currentStatus ? ` · ${title(data.shipment.currentStatus)}` : ''}</span>}
      </div>
    </div>
  );
}

function CustomersPage({ api, canOpen, onOpenTicket }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);
  const [sel, setSel] = useState(null);
  const [detail, setDetail] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [convos, setConvos] = useState(null);

  const open = useCallback(async (c) => {
    setSel(c);
    setDetail(null);
    setConvos(null);
    setOrderId(c.associatedOrderId || null);
    try {
      const d = await api(`/api/v1/support/customers/${encodeURIComponent(c.id || c.shopifyCustomerId)}/orders`);
      setDetail({ customer: d.customer || {}, orders: d.orders || [] });
    } catch (err) {
      setDetail({ customer: {}, orders: [], error: err.message });
    }
    const email = String(c.email || '').trim().toLowerCase();
    if (canOpen && email) {
      api('/api/v1/support/inbox')
        .then((d) => setConvos((d.items || []).filter((i) => String(i.email || '').toLowerCase() === email)))
        .catch(() => setConvos([]));
    } else setConvos([]);
  }, [api, canOpen]);

  const search = async (e) => {
    e?.preventDefault();
    const term = q.trim();
    if (!term) return;
    setSearching(true);
    setError(null);
    try {
      const d = await api(`/api/v1/support/customers/search?q=${encodeURIComponent(term)}`);
      const list = d.customers || [];
      setResults(list);
      if (list.length === 1) open(list[0]);
      else setSel(null);
    } catch (err) {
      setError(err.message);
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const c = sel ? { ...sel, ...(detail?.customer || {}) } : null;
  const orders = detail?.orders || [];
  const addr = c?.defaultAddress ? [c.defaultAddress.city, c.defaultAddress.province, c.defaultAddress.country].filter(Boolean).join(', ') : '';

  return (
    <div className={`cx-cust${sel ? ' has-sel' : ''}`}>
      <section className="cx-cust-list" aria-label="Customer search">
        <form onSubmit={search} role="search">
          <h1>Customers</h1>
          <label className="cx-search"><Icon name="search" size={16} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, email, phone or order number" aria-label="Search customers" autoFocus /></label>
          <button className="cx-btn cx-btn-primary" type="submit" disabled={searching || !q.trim()} style={{ justifyContent: 'center' }}>{searching ? 'Searching…' : 'Search'}</button>
          {error && <span className="cx-err">{error}</span>}
        </form>
        <div className="cx-rows">
          {searching ? <><div className="cx-skel" /><div className="cx-skel" /></> : results === null ? (
            <div className="cx-empty"><strong>Find a customer</strong>Search by name, email, phone, or an order number like 7501300.</div>
          ) : results.length === 0 ? (
            <div className="cx-empty"><strong>No customers found</strong>Check the spelling, or try their email or order number.</div>
          ) : results.map((r) => (
            <div key={r.id} className="cx-row" role="option" tabIndex={0} aria-selected={sel?.id === r.id} onClick={() => open(r)} onKeyDown={(e) => e.key === 'Enter' && open(r)}>
              <div className="cx-row-av"><ChannelBadge channel="shop" large="xl" /></div>
              <div style={{ minWidth: 0 }}>
                <div className="cx-row-top"><span className="cx-row-name">{nameOf(r)}</span>{r.associatedOrderNumber && <span className="cx-row-time cx-mono">{r.associatedOrderNumber}</span>}</div>
                <div className="cx-row-prev">{[r.email, r.phone].filter(Boolean).join(' · ') || 'No contact details'}</div>
                {(r.ordersCount != null || r.totalSpent) && <div className="cx-row-tags">{r.ordersCount != null && <span className="cx-chip cx-c-plain">{r.ordersCount} orders</span>}{r.totalSpent && <span className="cx-chip cx-c-plain">{money(r.totalSpent)}</span>}</div>}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="cx-cust-detail" aria-label="Customer details">
        {!c ? (
          <div className="cx-empty" style={{ paddingTop: 96 }}><strong>No customer selected</strong>Search on the left to see orders, production and past conversations.</div>
        ) : (
          <div className="cx-page-in">
            <button className="cx-btn cx-btn-ghost cx-back-cust" onClick={() => setSel(null)} style={{ marginBottom: 12 }}><Icon name="back" size={16} />Results</button>
            <div className="cx-cust-hero">
              <ChannelBadge channel="shop" large="xl" />
              <div style={{ minWidth: 0 }}>
                <h2>{nameOf(c)}</h2>
                <span>{[c.email, c.phone, addr].filter(Boolean).join(' · ') || 'No contact details'}</span>
              </div>
            </div>
            <div className="cx-metrics" style={{ marginTop: 18 }}>
              <div className="cx-metric"><span>Orders</span><b>{c.ordersCount ?? (detail ? orders.length : '—')}</b></div>
              <div className="cx-metric"><span>Total spent</span><b>{c.totalSpent ? money(c.totalSpent) : '—'}</b></div>
              <div className="cx-metric"><span>Last order</span><b style={{ fontSize: 20 }}>{orders[0] ? fmtDate(orders[0].createdAt) : '—'}</b></div>
              <div className="cx-metric"><span>Conversations</span><b>{convos ? convos.length : '—'}</b><small>{convos ? `${convos.filter((x) => x.status !== 'resolved').length} open` : ''}</small></div>
            </div>

            <section className="cx-panel" style={{ marginTop: 20 }} aria-labelledby="cx-ord-h">
              <div className="cx-panel-h"><h2 id="cx-ord-h">Orders</h2><span>Click an order for production details</span></div>
              {!detail ? <div className="cx-skel" style={{ height: 120 }} /> : detail.error ? <div className="cx-panel-b cx-err">{detail.error}</div> : orders.length === 0 ? (
                <div className="cx-empty" style={{ padding: 32 }}><strong>No orders yet</strong>Orders placed on the website show here.</div>
              ) : (
                <div className="cx-scroll-x">
                  <table className="cx-table">
                    <thead><tr><th>Order</th><th>Date</th><th>Status</th><th className="r">Total</th><th className="r">Items</th><th>Tracking</th></tr></thead>
                    <tbody>
                      {orders.map((o) => {
                        const id = o.shopifyOrderId || o.id;
                        return (
                          <React.Fragment key={id || o.orderNumber}>
                            <tr className="click" aria-selected={orderId === id} onClick={() => setOrderId(orderId === id ? null : id)} tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && setOrderId(orderId === id ? null : id)}>
                              <td className="cx-mono" style={{ color: 'var(--acc-700)', fontWeight: 500 }}>{o.orderNumber || o.name || '—'}</td>
                              <td>{fmtDate(o.createdAt)}</td>
                              <td><span className={`cx-chip ${fulfilTone(o.fulfillmentStatus || o.status)}`}>{title(o.fulfillmentStatus || o.status || 'unknown')}</span></td>
                              <td className="r cx-num">{money(o.totalPrice, o.currency)}</td>
                              <td className="r cx-num">{o.lineItemCount ?? o.lineItems?.length ?? '—'}</td>
                              <td className="cx-mono" style={{ color: 'var(--ink-3)' }}>{o.trackingNumber || '—'}</td>
                            </tr>
                            {orderId === id && <tr><td colSpan={6} style={{ padding: 0, background: 'var(--panel)' }}><Production api={api} orderId={id} /></td></tr>}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {canOpen && (
              <section className="cx-panel" style={{ marginTop: 20 }} aria-labelledby="cx-conv-h">
                <div className="cx-panel-h"><h2 id="cx-conv-h">Conversations</h2><span>matched by email</span></div>
                {convos === null ? <div className="cx-skel" style={{ height: 60 }} /> : convos.length === 0 ? (
                  <div className="cx-empty" style={{ padding: 28 }}><strong>No conversations</strong>{c.email ? 'Nothing from this email address yet.' : 'This customer has no email to match on.'}</div>
                ) : convos.map((x) => (
                  <div key={x.id} className="cx-need">
                    <ChannelBadge channel={x.channel} />
                    <div className="cx-need-main">
                      <div>{x.caseRef && <b className="cx-mono" style={{ fontWeight: 500 }}>{x.caseRef}</b>}<span>{x.subject || x.preview}</span><span className={`cx-chip cx-c-${x.status}`}>{STATUS_LABEL[x.status]}</span></div>
                      <div>{fmtDate(x.lastActivityMs)}</div>
                    </div>
                    <button className="cx-btn cx-btn-sm" onClick={() => onOpenTicket(x.id)}>Open</button>
                  </div>
                ))}
              </section>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

export default CustomersPage;
